import React, { useState, useEffect } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import { SubscriptionStep } from './steps/SubscriptionStep';
import { AddKidsStep } from './steps/AddKidsStep';
import { ClassEnrollmentStep } from './steps/ClassEnrollmentStep';
import { PaymentStep } from './steps/PaymentStep';
import { PendingApprovalScreen } from './steps/PendingApprovalScreen';
import { useSubscriptions } from '@/hooks/useSubscriptions';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Props {
    variant?: 'fullscreen' | 'overlay';
}

export const OnboardingWizard: React.FC<Props> = ({ variant = 'fullscreen' }) => {
    const { t } = useTranslation();
    const { currentUser } = useAuth();
    const { userSubscriptions } = useSubscriptions();
    const [searchParams] = useSearchParams();
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [loading, setLoading] = useState(true);
    const [targetKidIds, setTargetKidIds] = useState<string[]>([]);

    useEffect(() => {
        if (!currentUser) return;

        // URL override
        const urlStep = searchParams.get('step');
        if (urlStep !== null) {
            setCurrentStep(parseInt(urlStep));
            setLoading(false);
            return;
        }

        // Determine initial step based on user state
        const checkProgress = async () => {
            setLoading(true);
            try {
                // Check if user has an active subscription
                const hasSubscription =
                    currentUser.subscriptionStatus === 'active' ||
                    (userSubscriptions && userSubscriptions.some(s => s.userId === currentUser.uid && s.status === 'active' && s.endDate > new Date()));

                // Check if user is a parent (has kids linked) or if they're signing up for themselves
                // For students signing up for themselves, skip the "Add Kids" step (step 0)
                // and go directly to subscription selection (now step 0 in student flow)
                // A user is considered a "student signing up for themselves" if:
                // - They are NOT a kid profile (isKidProfile !== true)
                // - They don't have parentId (not managed by a parent)
                // - They are a regular student, not a parent managing family
                // For new signups, we'll assume they're signing up for themselves unless they explicitly add kids
                const isSigningUpForSelf = !currentUser.isKidProfile && !currentUser.parentId;

                if (hasSubscription) {
                    // Already has subscription, go to class enrollment
                    setCurrentStep(isSigningUpForSelf ? 2 : 2);
                } else {
                    // No subscription - Students start at Subscription (step 1), Parents start at AddKids (step 0)
                    if (isSigningUpForSelf) {
                        setTargetKidIds([currentUser.uid]); // Student is their own target
                        setCurrentStep(1); // Skip AddKidsStep, go directly to SubscriptionStep
                    } else {
                        setCurrentStep(0); // Parents/kids start at AddKidsStep
                    }
                }
            } catch (err) {
                console.error("Error checking onboarding progress:", err);
            } finally {
                setLoading(false);
            }
        };

        checkProgress();
    }, [currentUser, userSubscriptions, searchParams]);

    const nextStep = () => {
        setCurrentStep(prev => prev + 1);
    };

    const handleAddKidsNext = (ids?: string[]) => {
        if (ids && ids.length > 0) {
            setTargetKidIds(ids);
        }
        nextStep();
    };

    const prevStep = () => {
        setCurrentStep(prev => Math.max(0, prev - 1));
    };

    if (loading) {
        return <div className="flex justify-center items-center min-h-[50vh]">Loading...</div>;
    }

    // Wizard Steps
    // 0: Subscription
    // 1: Class Enrollment
    // 2: Payment
    // 3: Pending Approval Message

    const containerClasses = variant === 'fullscreen'
        ? "min-h-screen bg-gray-50 flex flex-col items-center py-10 px-4 overflow-y-auto"
        : "fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto"; // Overlay mode

    return (
        <div className={containerClasses}>
            <div className="w-full max-w-4xl bg-white rounded-xl shadow-lg p-6 md:p-10">
                {/* Progress Indicator */}
                <div className="mb-8">
                    <div className="flex items-center justify-between relative">
                        {/* Line */}
                        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-1 bg-gray-200 -z-10" />

                        {/* Steps */}
                        {[0, 1, 2, 3].map((step) => {
                            let status = 'pending';
                            if (currentStep > step) status = 'completed';
                            if (currentStep === step) status = 'current';

                            return (
                                <div key={step} className={`w-10 h-10 rounded-full flex items-center justify-center font-bold border-2 transition-colors duration-300 bg-white
                                    ${status === 'completed' ? 'border-green-500 text-green-500' : ''}
                                    ${status === 'current' ? 'border-primary text-primary shadow-md scale-110' : ''}
                                    ${status === 'pending' ? 'border-gray-300 text-gray-300' : ''}
                                 `}>
                                    {status === 'completed' ? '✓' : step + 1}
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex justify-between mt-2 text-xs font-medium text-gray-500 px-1">
                        <span>{t('onboarding.step_short.add')}</span>
                        <span>{t('onboarding.step_short.plan')}</span>
                        <span>{t('onboarding.step_short.class')}</span>
                        <span>{t('onboarding.step_short.pay')}</span>
                    </div>
                </div>

                {/* Content */}
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {/* Parent Flow: Step 0 is Kids */}
                        {currentStep === 0 && <AddKidsStep onNext={handleAddKidsNext} />}

                        {/* Standard Flow shifted by 1 */}
                        {currentStep === 1 && <SubscriptionStep onComplete={nextStep} targetUserIds={targetKidIds} />}
                        {currentStep === 2 && <ClassEnrollmentStep onComplete={nextStep} onBack={prevStep} targetUserIds={targetKidIds} />}
                        {currentStep === 3 && <PaymentStep onComplete={nextStep} targetUserIds={targetKidIds} />}
                        {currentStep >= 4 && <PendingApprovalScreen onBack={prevStep} />}
                    </div>
                </div>
            </div>
        </div>
    );
};
